import React, { useState, useEffect } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { connect } from 'containers/store'

import { FieldSection } from 'components/ui/fieldset'
import { Input } from 'components/ui/input'

import { MediaField } from 'domains/media/components/media-field'

import { Media } from 'generated/mos/media'
import { VisitorInfoPage } from 'generated/mos/org/getty/visitorinfo'
import { UpdateVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfomanagement'

import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { convertEntityToFormValues } from 'helpers/schema'
import { Status } from 'helpers/status'

import { actionCreators, GettyActionCreators } from '../../../../actions'

import { GettyStore } from '../../../../store'

import { deepDivePageSchema } from '../../../../schemas/visitorInfo'

type FormValues = {
  headerTitle: string | undefined;
  headerImageAltText?: string | undefined;
  descriptionText: string | undefined;
  listTitle: string | undefined;
}

type ActionProps =
  Pick<GettyActionCreators, 'gettyUpdateVisitorInfoPageRequest'>

type ConnectedProps = Pick<GettyStore['getty'], 'visitorInfoUpdate'>

type DirectProps = {
  readonly page: VisitorInfoPage.Entity | undefined;
  readonly setSubmit: (submit: () => void) => void;
  readonly setHasSubmitted: (submitted: boolean) => void;
  readonly setModified: (modifed: boolean | undefined) => void;
}

type Props = ActionProps & ConnectedProps & DirectProps

const DeepDivePageUpdateView = (props: Props) => {
  const {
    visitorInfoUpdate,
    gettyUpdateVisitorInfoPageRequest,
    page,
    setSubmit,
    setHasSubmitted,
    setModified,
  } = props

  // set up form values
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ imageMedia, setImageMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ iconMedia, setIconMedia ] = useState<Media.Ref | undefined>(undefined)

  const pageValues: Partial<VisitorInfoPage.DeepDivePage.Entity & VisitorInfoPage.Entity> = pick({
    ...VisitorInfoPage.DeepDivePage.defaults,
    ...VisitorInfoPage.defaults,
    ...page,
    ...(page && page.pageProperties) ? page.pageProperties: null
  }, 'headerTitle', 'headerImageAltText', 'descriptionText', 'listTitle')

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = page ?
    convertEntityToFormValues<VisitorInfoPage.DeepDivePage.Entity & VisitorInfoPage.Entity | VisitorInfoPage.DeepDivePage.Entity>(pageValues, deepDivePageSchema) as any as FormValues:
    undefined
  const pageProperties = page && page.pageProperties as VisitorInfoPage.DeepDivePage.Entity
  const initialImageValue = page && pageProperties && deepDivePageSchema.headerImageRef.read(pageProperties.headerImageRef) as any as Media.Ref | undefined
  const initialIconValue = page && deepDivePageSchema.listIconRef.read(page.listIconRef) as any as Media.Ref | undefined

  if (page && initialFormValues && !formBag && visitorInfoUpdate.status === Status.Ready) {
    setFormBag(createFormBag(initialFormValues))
    setImageMedia(initialImageValue)
    setIconMedia(initialIconValue)
  }

  useEffect(() => {
    setSubmit(() => () => onSubmit())
  })

  // handle the form ui.
  const modified = initialFormValues && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialImageValue, imageMedia) ||
    !areRefValuesEqual(initialIconValue, iconMedia)
  )

  setModified(modified)

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {};

    if (!values.listTitle || !values.listTitle.trim().length) {
      errors.listTitle = 'List title is required'
    }

    return errors
  }

  const onSubmit = () => {
    return formBag && page && submit(formBag, page)
  }

  const submit = (formBag: FormBag<FormValues>, page: VisitorInfoPage.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if(!bag.valid) {
      return false
    }

    const pageProperties = page && page.pageProperties as VisitorInfoPage.DeepDivePage.Entity
    const { listTitle, headerTitle, headerImageAltText, descriptionText} = formBag.values

    setHasSubmitted(true)

    gettyUpdateVisitorInfoPageRequest({
      ...UpdateVisitorInfoPageRequest.defaults,
      visitorInfoPageRef: VisitorInfoPage.mustRef(page.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'listIconRef',
          'listTitle',
          'deepDivePage',
        ]
      },
      listIconRef: deepDivePageSchema.listIconRef.write(iconMedia) || iconMedia,
      listTitle: deepDivePageSchema.listTitle.write(listTitle, page.listTitle) || listTitle,
      pageProperties: {
        ...VisitorInfoPage.DeepDivePage.defaults,
        headerTitle: deepDivePageSchema.headerTitle.write(headerTitle, pageProperties.headerTitle) || headerTitle,
        headerImageAltText: deepDivePageSchema.headerImageAltText.write(headerImageAltText, pageProperties.headerImageAltText) || headerImageAltText,
        descriptionText: deepDivePageSchema.descriptionText.write(descriptionText, pageProperties.descriptionText) || descriptionText,
        headerImageRef: deepDivePageSchema.headerImageRef.write(imageMedia) || imageMedia,
      }
    })
    return false
  }

  return formBag && page && (
    <form noValidate onSubmit={(event) => { event.preventDefault() }}>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
          setSubmit(() => () => onSubmit())
          setModified(modified)
        }}
        validate={() => ({})}
      >
        <Input
          label="Title"
          field="headerTitle"
          error={formBag.touched.headerTitle ? formBag.errors.headerTitle : undefined}
        />
        <MediaField
          kind="MEDIA_KIND_RASTER_IMAGE"
          mediaRef={imageMedia}
          onUpdate={(entity) => { setImageMedia(entity ? entity.ref : undefined) }}
          label="Image"
        />
        <Input
          label="Alternate Text"
          description="Description of what the image contains if it cannot be shown."
          field="headerImageAltText"
          isAssociated={true}
        />
        <Input
          component="textarea"
          label="Description"
          field="descriptionText"
        />
        <FieldSection />
        <Input
          label="List title"
          field="listTitle"
          error={formBag.touched.listTitle ? formBag.errors.listTitle : undefined}
          description="The list title is the page's name in the navigation menu."
        />
        <MediaField
          kind="MEDIA_KIND_RASTER_IMAGE"
          mediaRef={iconMedia}
          onUpdate={(entity) => {
            setIconMedia(entity ? entity.ref : undefined)
            setSubmit(() => () => onSubmit())
          }}
          label="List icon"
        />
      </FormData>
    </form>
  )
}

export const DeepDivePageUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'visitorInfoUpdate'),
  {
    ...pick(actionCreators, 'gettyUpdateVisitorInfoPageRequest'),
  },
)(DeepDivePageUpdateView)
